import { useCollection } from '@amzn/awsui-collection-hooks';
import { Box, Button } from '@amzn/awsui-components-console';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import messages from '@/components/AttendanceTable/messages';

import type { CollectionPreferencesProps } from '@amzn/awsui-components-console';

interface UseAttendanceCollectionOptions {
    filtering?: {
        fields: string[];
    };
    sorting?: {
        defaultState?: {
            sortingColumn: any;
            isDescending?: boolean;
        };
    };
}

interface TableSettings {
    preferences: {
        pageSize: number;
    };
    collectionProps: any;
    paginationProps: any;
    collectionPreferenceProps: CollectionPreferencesProps;
    filterProps?: any;
    actions?: any;
}

interface UseAttendanceCollectionReturn {
    displayItems: readonly any[];
    tableSettings: TableSettings;
}
/**
 * This hook handles the UI components for attendance tables. Sets defaults for most values
 * and provides options for values that need to be different between tables (mainly columnn info)
 * @param attendanceRecords
 * @param options
 * @returns
 */
export const useAttendanceCollection = (
    attendanceRecords: any[],
    options: UseAttendanceCollectionOptions = {},
): UseAttendanceCollectionReturn => {
    const pageSizeOptions = [10, 20, 50, 100];
    const defaultPageSize = 10;

    const { formatMessage } = useIntl();

    const [preferences, setPreferences] = useState({
        pageSize: defaultPageSize,
    });

    const { filtering, sorting } = options;

    // Use collection does the heavy lifting of creating the table options
    const { items, actions, collectionProps, paginationProps, filterProps } = useCollection(
        attendanceRecords,
        {
            pagination: { pageSize: preferences.pageSize },
            selection: {},
            sorting: sorting || {},
            filtering: filtering?.fields
                ? {
                      fields: filtering.fields,
                      empty: (
                          <Box textAlign='center' color='inherit'>
                              <Box variant='strong' textAlign='center' color='inherit'>
                                  {formatMessage(messages.empty)}
                              </Box>
                          </Box>
                      ),
                      noMatch: (
                          <Box textAlign='center' color='inherit'>
                              <Box variant='p' padding={{ bottom: 's' }} color='inherit'>
                                  {formatMessage(messages.filterNoMatch)}
                              </Box>
                              <Button onClick={() => actions.setFiltering('')}>
                                  {formatMessage(messages.filterClear)}
                              </Button>
                          </Box>
                      ),
                  }
                : undefined,
        },
    );

    const itemsTitle = formatMessage(messages.itemsLabel);

    // Contents of the little gear icon
    const collectionPreferenceProps: CollectionPreferencesProps = {
        title: formatMessage(messages.preferencesTitle),
        confirmLabel: formatMessage(messages.confirmLabel),
        cancelLabel: formatMessage(messages.cancelLabel),
        preferences: {
            pageSize: preferences.pageSize,
        },
        pageSizePreference: {
            title: formatMessage(messages.pageSizeLabel),
            options: pageSizeOptions.map((size) => ({
                value: size,
                label: `${size} ${itemsTitle}`,
            })),
        },
        onConfirm: ({ detail }: { detail: any }) => {
            setPreferences(detail);
        },
    };

    return {
        displayItems: items,
        tableSettings: {
            preferences,
            collectionProps,
            paginationProps,
            collectionPreferenceProps,
            filterProps: filtering?.fields ? filterProps : undefined,
        },
    };
};
